import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import {
  getClanReports,
  handleClanReport,
  ClanReport,
} from '../../../../api/report/report-clan.api';
import { Shield } from 'lucide-react';
import { AdminButton, AdminPageHeader, AdminPillTabs } from '../../ui';
import { KvRow, ReasonPill, ReportKv } from '../report-moderation/ReportParts';


const REASON_LABEL: Record<number, string> = {
  0: 'Spam',
  1: 'Không phù hợp',
  2: 'Thù ghét (toxic)',
  3: 'Bạo lực',
  4: 'Quấy rối',
  5: 'Vi phạm SHTT',
  6: 'Lừa đảo / spam',
  7: 'Sai lệch thông tin',
  8: 'Nguy hại trẻ em',
  9: 'Nội dung nhạy cảm',
  10: 'Khác',
};

const STATE_TABS = [
  { label: 'Chờ duyệt', value: 0 },
  { label: 'Đã xử lý', value: 1 },
  { label: 'Đã bỏ qua', value: 2 },
];

const ManagementReportClan = () => {
  const [state, setState] = useState<number>(0);
  const [items, setItems] = useState<ClanReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getClanReports(state, 1, 50)
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [state]);

  useEffect(() => {
    load();
  }, [load]);

  const onHandle = async (id: string, accept: boolean) => {
    setBusyId(id);
    try {
      await handleClanReport(id, accept);
      toast.success(accept ? 'Đã đánh dấu xử lý bang' : 'Đã bỏ qua báo cáo');
      load();
    } catch {
      toast.error('Thao tác thất bại');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Clan bị báo cáo"
        description="Xem lý do báo cáo từng Clan rồi đánh dấu đã xử lý hoặc bỏ qua."
      />

      <section className="adm-stack">
        <AdminPillTabs
          ariaLabel="Lọc trạng thái báo cáo"
          items={STATE_TABS.map((t) => ({ key: t.value, label: t.label }))}
          value={state}
          onChange={(k) => setState(k)}
        />

        {loading ? (
          <p className="adm-report-empty">Đang tải…</p>
        ) : items.length === 0 ? (
          <p className="adm-report-empty">Không có báo cáo nào.</p>
        ) : (
          <div className="adm-report-grid">
            {items.map((r) => (
              <div key={r.id} className="adm-report-card">
                <div className="adm-report-card__head">
                  <div className="adm-report-card__icon adm-report-card__icon--square">
                    <Shield size={20} aria-hidden="true" />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="adm-report-card__caption">Bang</div>
                    <Link to={`/clan/${r.clanId}`} className="adm-report-link" style={{ fontSize: 16, lineHeight: '22px' }}>
                      {r.clanName || r.clanId.slice(0, 8)}
                    </Link>
                  </div>
                </div>
                <ReportKv>
                  <KvRow label="Người báo cáo">{r.reportByUserName || r.reportByUserId.slice(0, 8)}</KvRow>
                  <KvRow label="Lý do">
                    <ReasonPill>{REASON_LABEL[r.reason] ?? 'Khác'}</ReasonPill>
                  </KvRow>
                  <KvRow label="Thời gian">
                    <span className="adm-report-kv__muted">{new Date(r.createdAt).toLocaleString('vi-VN')}</span>
                  </KvRow>
                </ReportKv>
                {r.content && <div className="adm-report-quote">“{r.content}”</div>}
                {state === 0 && (
                  <div className="adm-report-actions">
                    <AdminButton variant="danger" size={36} disabled={busyId === r.id} onClick={() => onHandle(r.id, true)}>
                      Đánh dấu xử lý
                    </AdminButton>
                    <AdminButton variant="outline" size={36} disabled={busyId === r.id} onClick={() => onHandle(r.id, false)}>
                      Bỏ qua
                    </AdminButton>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ManagementReportClan;
